import React, { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import "./facilityVip.css";
import {
  ArrowLeft,
  Loader,
  Send,
  ChevronRight,
  UploadCloud,
  FileText,
  Receipt,
  Eye,
  Download,
} from "lucide-react";
import { toast } from "react-toastify";

/* ⚠️ Remplace par la classe/ID réel de la grande zone arrondie du dashboard
   (celle que le backdrop de Unlimited Bonus recouvre).
   Si l'élément n'est pas trouvé, le fond couvre tout l'écran. */
const DASHBOARD_SELECTOR = ".TON-SELECTEUR-DASHBOARD";

/* Shell commun (défini HORS du composant pour garder le focus des champs) */
const Shell = ({ title, onBack, children }) => (
  <div className="fv-modal">
    <header className="fv-header">
      <button
        type="button"
        className="fv-back"
        aria-label="Retour"
        onClick={onBack}
      >
      </button>
      <h3 className="fv-title">{title}</h3>
      <span className="fv-header-spacer" />
    </header>
    <div className="fv-body">{children}</div>
  </div>
);

const QUICK_POINTS = [30, 50, 100, 200];

const FacilityVip = ({ onClose }) => {
  const [viewMode, setViewMode] = useState("initial");
  const [facilityVip, setFacilityVip] = useState(null);
  const [reports, setReports] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [error, setError] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [userId, setUserId] = useState(null);

  const [showRechargePopup, setShowRechargePopup] = useState(false);
  const [rechargePoints, setRechargePoints] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [showRechargeConfirm, setShowRechargeConfirm] = useState(false);
  const [rechargeTimer, setRechargeTimer] = useState(0);

  /* ── Upload reçu de paiement d'une facture ── */
  const [uploadInvoice, setUploadInvoice] = useState(null); // facture sélectionnée
  const [invoiceFile, setInvoiceFile] = useState(null); // fichier choisi
  const [isUploading, setIsUploading] = useState(false);
  const [invoicesReload, setInvoicesReload] = useState(0); // rafraîchir la liste

  /* ── Backdrop : position/taille de la zone dashboard ── */
  const [bdRect, setBdRect] = useState(null);

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000/api";

  const formatDate = (dateString) => {
    const d = new Date(dateString);
    return `${String(d.getDate()).padStart(2, "0")}.${String(
      d.getMonth() + 1,
    ).padStart(2, "0")}.${d.getFullYear()}`;
  };

  /* ── BACKDROP RECT ── */
  useEffect(() => {
    const measure = () => {
      const el = document.querySelector(DASHBOARD_SELECTOR);
      if (!el) {
        setBdRect(null);
        return;
      }
      const r = el.getBoundingClientRect();
      setBdRect({
        top: r.top,
        left: r.left,
        width: r.width,
        height: r.height,
      });
    };
    measure();
    window.addEventListener("resize", measure);
    return () => window.removeEventListener("resize", measure);
  }, []);

  /* ── AUTH ── */
  useEffect(() => {
    const token = localStorage.getItem("token");
    if (!token) {
      setError("No authentication token found.");
      return;
    }
    try {
      setUserId(jwtDecode(token).id);
    } catch {
      setError("Invalid token.");
    }
  }, []);

  /* ── HISTORY ── */
  useEffect(() => {
    if (userId && viewMode === "history") {
      (async () => {
        setIsLoading(true);
        try {
          const res = await axios.get(`${API_URL}/facility-vip/${userId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          setFacilityVip(res.data.data);
        } catch (err) {
          setError(err.response?.data?.message || "Failed to load history.");
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [userId, viewMode]);

  /* ── REPORTS ── */
  useEffect(() => {
    if (userId && viewMode === "report") {
      (async () => {
        setIsLoading(true);
        try {
          const res = await axios.get(`${API_URL}/reports/${userId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          setReports(res.data.data || []);
          setError(null);
        } catch {
          setError("Cannot load reports.");
          setReports([]);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [userId, viewMode]);

  /* ── INVOICES ── */
  useEffect(() => {
    if (userId && viewMode === "invoices") {
      (async () => {
        setIsLoading(true);
        try {
          const res = await axios.get(`${API_URL}/invoices/user/${userId}`, {
            headers: {
              Authorization: `Bearer ${localStorage.getItem("token")}`,
            },
          });
          setInvoices(
            (res.data.invoices || []).filter(
              (inv) => (inv.invoiceType || "general") === "general",
            ),
          );
          setError(null);
        } catch (err) {
          setError(err.response?.data?.message || "Cannot load invoices.");
          setInvoices([]);
        } finally {
          setIsLoading(false);
        }
      })();
    }
  }, [userId, viewMode, invoicesReload]);

  /* ── NOTIFICATIONS ── */
  useEffect(() => {
    if (!userId || viewMode !== "history") return;
    const check = async () => {
      try {
        const res = await axios.get(`${API_URL}/point-recharge/my-requests`, {
          headers: {
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        });
        (res.data.data || []).forEach((r) => {
          if (!["accepted", "rejected"].includes(r.status)) return;
          const key = `notif_recharge_${r._id}`;
          if (localStorage.getItem(key)) return;
          toast[r.status === "accepted" ? "success" : "error"](
            r.status === "accepted"
              ? `Recharge of ${r.points} pts accepted!`
              : `Recharge rejected. ${r.adminMessage || ""}`,
            { position: "top-center", autoClose: 5000 },
          );
          localStorage.setItem(key, "true");
        });
      } catch {}
    };
    check();
    const iv = setInterval(check, 15000);
    return () => clearInterval(iv);
  }, [userId, viewMode]);

  /* ── TIMER ── */
  useEffect(() => {
    if (rechargeTimer <= 0) return;
    const iv = setInterval(() => {
      setRechargeTimer((p) => {
        if (p <= 1) {
          toast.error("Code expired.");
          return 0;
        }
        return p - 1;
      });
    }, 1000);
    return () => clearInterval(iv);
  }, [rechargeTimer]);

  /* ── HANDLERS ── */
  const closeInvoiceUpload = () => {
    setUploadInvoice(null);
    setInvoiceFile(null);
  };

  const handleBack = () => {
    setViewMode("initial");
    setShowRechargePopup(false);
    setShowRechargeConfirm(false);
    setRechargePoints("");
    setSelectedFile(null);
    setError(null);
    closeInvoiceUpload();
  };

  const goBackToAmount = () => {
    setShowRechargeConfirm(false);
    setShowRechargePopup(true);
    setSelectedFile(null);
  };

  const handleRechargeSubmit = async () => {
    if (!selectedFile || !rechargePoints) return;
    const formData = new FormData();
    formData.append("points", rechargePoints);
    formData.append("receipt", selectedFile);
    try {
      const res = await fetch(`${API_URL}/point-recharge`, {
        method: "POST",
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        toast.success("Receipt sent! Admin will review it.", {
          position: "top-center",
          autoClose: 3000,
        });
        setShowRechargeConfirm(false);
        setShowRechargePopup(false);
        setSelectedFile(null);
        setRechargePoints("");
      } else {
        toast.error(data.message || "Error sending receipt", {
          position: "top-center",
          autoClose: 3000,
        });
      }
    } catch {
      toast.error("Network error");
    }
  };

  const submitStep1 = () => {
    if (!rechargePoints?.trim()) {
      toast.error("Please enter a number of points.", {
        position: "top-center",
      });
      return;
    }
    const p = parseInt(rechargePoints);
    if (isNaN(p) || p < 30) {
      toast.error("Minimum 30 points required.", { position: "top-center" });
      return;
    }
    setShowRechargeConfirm(true);
    setShowRechargePopup(false);
  };

  const handleViewInvoice = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/invoices/view/${invoiceId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to open invoice.");
    }
  };

  const handleDownloadInvoice = async (invoiceId) => {
    try {
      const res = await axios.get(`${API_URL}/invoices/download/${invoiceId}`, {
        headers: { Authorization: `Bearer ${localStorage.getItem("token")}` },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(
        new Blob([res.data], { type: "application/pdf" }),
      );
      const link = document.createElement("a");
      link.href = url;
      link.download = `invoice-${invoiceId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.message || "Unable to download invoice.");
    }
  };

  /* Envoi du reçu de paiement (même méthode que ClientInvoices : PUT /invoices/:id) */
  const handleInvoiceReceiptSubmit = async () => {
    if (!invoiceFile || !uploadInvoice || isUploading) return;

    const formData = new FormData();
    formData.append("status", "pending");
    formData.append("receipt", invoiceFile);
    formData.append("invoiceDate", uploadInvoice.invoiceDate);

    setIsUploading(true);
    try {
      await axios.put(`${API_URL}/invoices/${uploadInvoice._id}`, formData, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "Content-Type": "multipart/form-data",
        },
      });

      closeInvoiceUpload(); // ferme le popup d'upload
      setInvoicesReload((n) => n + 1); // rafraîchit la liste
      toast.success(
        "Receipt submitted successfully! Waiting for admin verification.",
        { position: "top-center", autoClose: 5000 },
      );
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to submit receipt.", {
        position: "top-center",
        autoClose: 3000,
      });
    } finally {
      setIsUploading(false);
    }
  };

  const pts = parseInt(rechargePoints) || 0;
  const totalMillimes = pts * 1600 + 1000;

  const menuItems = [
    {
      img: "images/services/3.jpg",
      t: "Facility Service",
      s: "Your points & balance",
      on: () => setViewMode("history"),
    },
    {
      img: "images/facilityService/RechargePoints.jpg",
      t: "Recharge Points",
      s: "Top up your account",
      on: () => setShowRechargePopup(true),
    },
    {
      img: "images/facilityService/ReportService.jpg",
      t: "Report Service",
      s: "Your PDF reports",
      on: () => setViewMode("report"),
    },
    {
      img: "/images/dashboard/mypayement.png",
      t: "Invoices",
      s: "Your invoices & payments",
      on: () => setViewMode("invoices"),
    },
  ];

  /* ═══════════════ RENDER (portal dans document.body) ═══════════════ */
  return createPortal(
    <>
      {/* ── BACKDROP (identique à .bonus-backdrop) ── */}
    <div
  className="fv-backdrop"
  style={{ borderRadius: 0 }}
  onClick={onClose}
/>

      {/* ── MENU ── */}
      {viewMode === "initial" && !showRechargePopup && !showRechargeConfirm && (
        <Shell title="Facility VIP" onBack={onClose}>
          <div className="fv-menu">
            {menuItems.map((item) => (
              <button
                key={item.t}
                type="button"
                className="fv-menu-item"
                onClick={item.on}
              >
                {item.img ? (
                  <img src={item.img} alt={item.t} className="fv-menu-icon" />
                ) : (
                  <span className="fv-menu-icon fv-menu-icon-svg">
                    {item.icon}
                  </span>
                )}
                <span className="fv-menu-text">
                  <strong>{item.t}</strong>
                  <small>{item.s}</small>
                </span>
                <ChevronRight size={15} className="fv-chevron" />
              </button>
            ))}
          </div>
        </Shell>
      )}

      {/* ── HISTORY ── */}
      {viewMode === "history" && (
        <Shell title="Facility Details" onBack={handleBack}>
          {isLoading ? (
            <div className="fv-state">
              <Loader className="fv-spin" size={16} /> Loading…
            </div>
          ) : facilityVip ? (
            <div className="fv-stats">
              <div className="fv-stat fv-stat-main">
                <img
                  src="images/facilityService/total.jpg"
                  alt="Total"
                  className="fv-stat-icon"
                />
                <div>
                  <span className="fv-stat-label">Total Points</span>
                  <span className="fv-stat-value">
                    {facilityVip.totalPoints}
                  </span>
                </div>
              </div>
              <div className="fv-stat">
                <span className="fv-stat-label">
                  <i className="fv-dot fv-dot-ok" /> Available
                </span>
                <span className="fv-stat-value">
                  {facilityVip.availablePoints}
                </span>
              </div>
              <div className="fv-stat">
                <span className="fv-stat-label">
                  <i className="fv-dot fv-dot-ko" /> Blocked
                </span>
                <span className="fv-stat-value">
                  {facilityVip.BlockedPoints}
                </span>
              </div>
            </div>
          ) : (
            <div className="fv-state">No data available.</div>
          )}
        </Shell>
      )}

      {/* ── RECHARGE 1 : MONTANT ── */}
      {showRechargePopup && !showRechargeConfirm && (
        <Shell title="Recharge Points" onBack={handleBack}>
          <label className="fv-field-label" htmlFor="fv-points">
            Number of points
          </label>
          <input
            id="fv-points"
            type="number"
            min="30"
            placeholder="e.g. 50"
            value={rechargePoints}
            onChange={(e) => {
              const v = e.target.value;
              if (v === "" || /^\d*$/.test(v)) setRechargePoints(v);
            }}
            className="fv-big-input"
          />

          <div className="fv-chips">
            {QUICK_POINTS.map((q) => (
              <button
                key={q}
                type="button"
                className={`fv-chip ${pts === q ? "active" : ""}`}
                onClick={() => setRechargePoints(String(q))}
              >
                {q}
              </button>
            ))}
          </div>

          <div className="fv-estimate">
            <span>Estimated total</span>
            <strong>
              {pts >= 30 ? `${(totalMillimes / 1000).toFixed(3)} TND` : "—"}
            </strong>
          </div>
          <p className="fv-hint">Minimum 30 points</p>

          <button
            type="button"
            className="fv-btn fv-btn-primary fv-btn-full"
            onClick={submitStep1}
          >
            Continue
          </button>
        </Shell>
      )}

      {/* ── RECHARGE 2 : RÉCAP + UPLOAD ── */}
      {showRechargeConfirm && (
        <Shell title="Confirm top-up" onBack={goBackToAmount}>
          <div className="fv-receipt">
            <div className="fv-receipt-row">
              <span>Points</span>
              <strong>{pts} pts</strong>
            </div>
            <div className="fv-receipt-row">
              <span>Price</span>
              <strong>{(pts * 1.6).toFixed(3)} TND</strong>
            </div>
            <div className="fv-receipt-row">
              <span>Fiscal stamp</span>
              <strong>1.000 TND</strong>
            </div>
            <div className="fv-receipt-total">
              <span>Total</span>
              <strong>{totalMillimes.toLocaleString()} millimes</strong>
            </div>
          </div>

          <label
            htmlFor="receipt-upload"
            className={`fv-dropzone ${selectedFile ? "has-file" : ""}`}
          >
            <UploadCloud size={18} />
            <span>
              {selectedFile ? selectedFile.name : "Upload your payment receipt"}
            </span>
            <small>{selectedFile ? "Click to change" : "Image or PDF"}</small>
          </label>
          <input
            type="file"
            id="receipt-upload"
            accept="image/*,.pdf"
            onChange={(e) => setSelectedFile(e.target.files?.[0] || null)}
            style={{ display: "none" }}
          />

          <div className="fv-actions">
            <button
              type="button"
              className="fv-btn fv-btn-ghost"
              onClick={goBackToAmount}
            >
              Cancel
            </button>
            <button
              type="button"
              className="fv-btn fv-btn-primary"
              onClick={handleRechargeSubmit}
              disabled={!selectedFile}
            >
              <Send size={13} /> Send receipt
            </button>
          </div>
        </Shell>
      )}

      {/* ── REPORTS ── */}
      {viewMode === "report" && (
        <Shell title="Reports" onBack={handleBack}>
          {error && <div className="fv-error">{error}</div>}
          {isLoading ? (
            <div className="fv-state">
              <Loader className="fv-spin" size={16} /> Loading…
            </div>
          ) : reports.length > 0 ? (
            <div className="fv-reports">
              {reports.map((report, i) => (
                <button
                  type="button"
                  key={`${report._id || i}-${report.pdfUrl}`}
                  className="fv-report"
                  onClick={() =>
                    window.open(
                      `http://localhost:5000${report.pdfUrl}`,
                      "_blank",
                      "noopener,noreferrer",
                    )
                  }
                >
                  <span className="fv-report-icon">
                    <FileText size={15} />
                  </span>
                  <span className="fv-report-text">
                    <strong>Report #{reports.length - i}</strong>
                    <small>{formatDate(report.createdAt)}</small>
                  </span>
                  <span className="fv-badge">PDF</span>
                </button>
              ))}
            </div>
          ) : (
            <div className="fv-state">No reports available.</div>
          )}
        </Shell>
      )}

      {/* ── INVOICE : UPLOAD REÇU ── */}
      {viewMode === "invoices" && uploadInvoice && (
        <Shell
          title={`Invoice — ${formatDate(uploadInvoice.invoiceDate)}`}
          onBack={closeInvoiceUpload}
        >
          <label
            htmlFor="invoice-receipt-upload"
            className={`fv-dropzone fv-dropzone-lg ${
              invoiceFile ? "has-file" : ""
            }`}
          >
            <UploadCloud size={22} />
            <span>{invoiceFile ? invoiceFile.name : "Upload receipt"}</span>
            <small>{invoiceFile ? "Click to change" : "JPG, PNG or PDF"}</small>
          </label>
          <input
            type="file"
            id="invoice-receipt-upload"
            accept="image/jpeg,image/png,.pdf"
            onChange={(e) => setInvoiceFile(e.target.files?.[0] || null)}
            style={{ display: "none" }}
          />

          <div className="fv-actions fv-actions-even">
            <button
              type="button"
              className="fv-btn fv-btn-ghost"
              onClick={closeInvoiceUpload}
              disabled={isUploading}
            >
              Cancel
            </button>
            <button
              type="button"
              className="fv-btn fv-btn-primary"
              onClick={handleInvoiceReceiptSubmit}
              disabled={!invoiceFile || isUploading}
            >
              {isUploading ? (
                <Loader className="fv-spin" size={13} />
              ) : (
                <Send size={13} />
              )}{" "}
              Submit
            </button>
          </div>
        </Shell>
      )}

      {/* ── INVOICES ── */}
      {viewMode === "invoices" && !uploadInvoice && (
        <Shell title="Invoices" onBack={handleBack}>
          {error && <div className="fv-error">{error}</div>}
          {isLoading ? (
            <div className="fv-state">
              <Loader className="fv-spin" size={16} /> Loading…
            </div>
          ) : invoices.length > 0 ? (
            <div className="fv-reports">
              {invoices.map((inv, i) => (
                <div key={inv._id || i} className="fv-report fv-invoice">
                  <span className="fv-report-icon">
                    <Receipt size={15} />
                  </span>
                  <span className="fv-report-text">
                    <strong>{formatDate(inv.invoiceDate)}</strong>
                    <small
                      className={
                        inv.status === "paid" ? "fv-paid" : "fv-pending"
                      }
                    >
                      {inv.status === "paid" ? "Paid" : "Pending"}
                    </small>
                  </span>

                  {inv.status === "pending" && (
                    <button
                      type="button"
                      className="fv-icon-btn fv-icon-btn-upload"
                      title="Upload payment receipt"
                      onClick={() => {
                        setUploadInvoice(inv);
                        setInvoiceFile(null);
                      }}
                    >
                      <UploadCloud size={14} />
                    </button>
                  )}
                  <button
                    type="button"
                    className="fv-icon-btn"
                    title="View"
                    onClick={() => handleViewInvoice(inv._id)}
                  >
                    <Eye size={14} />
                  </button>
                  <button
                    type="button"
                    className="fv-icon-btn"
                    title="Download"
                    onClick={() => handleDownloadInvoice(inv._id)}
                  >
                    <Download size={14} />
                  </button>
                </div>
              ))}
            </div>
          ) : (
            <div className="fv-state">No invoices available.</div>
          )}
        </Shell>
      )}
    </>,
    document.body,
  );
};

export default FacilityVip;